import { caseInsensitiveLocaleCompare, compareNumbers } from '../sort';

/**
 * Takes in any search result node from a search result list and return the corresponding
 * company key for that node, if one exists
 */
export function getCompanyKeyFromNode(searchResultNode: any): number | undefined {
  // data is not correct type
  if (!searchResultNode || typeof searchResultNode !== 'object') {
    return undefined;
  }

  // data does not have one of these two properties
  if (!searchResultNode.companyKey && !searchResultNode.eipCompanyKey) {
    return undefined;
  }

  return searchResultNode.isPerson || !searchResultNode.name
    ? parseInt(searchResultNode.companyKey, 10)
    : searchResultNode.eipCompanyKey;
}

/**
 * Returns a list of search results sorted alphabetically, or an empty array if the input is invalid.
 * @param searchResultTreeNodes the results to be sorted.
 */
export function getSearchResultsSortedAlphabetically(searchResultTreeNodes: ReadonlyArray<any>): ReadonlyArray<any> {
  return getSearchResultsSortedByCompareFunction(
    searchResultTreeNodes,
    (nodeA, nodeB) => caseInsensitiveLocaleCompare(nodeA.name, nodeB.name),
    { sortAscending: true, sortCompaniesOnly: true }
  );
}

/**
 * Returns an array of Search Result tree nodes sorted in the specified order using the given compare function
 * @param searchResultTreeNodes the search results to be sorted
 * @param compareFunction the function used for sorting the results
 * @param sortOptions optional argument for additional sorting options
 */
function getSearchResultsSortedByCompareFunction(
  searchResultTreeNodes: ReadonlyArray<any>,
  compareFunction: (nodeA: any, nodeB: any) => number,
  sortOptions: { sortAscending?: boolean; sortCompaniesOnly?: boolean } = {}
): ReadonlyArray<any> {
  const { sortAscending = true, sortCompaniesOnly = false } = sortOptions;
  if (!Array.isArray(searchResultTreeNodes)) {
    return [];
  }

  const sortedSearchResultTreeNodes = searchResultTreeNodes.sort((a, b) => {
    return sortAscending ? compareFunction(a, b) : compareFunction(b, a);
  });

  return sortedSearchResultTreeNodes.map(treeNode =>
    Array.isArray(treeNode.children)
      ? {
          ...treeNode,
          // keep people in order returned by elasticSearch, only recursively sort companies
          children: sortCompaniesOnly
            ? [
                ...treeNode.children.filter((child: { isPerson: boolean }) => child.isPerson),
                ...getSearchResultsSortedByCompareFunction(
                  treeNode.children.filter((child: { isPerson: boolean }) => !child.isPerson),
                  compareFunction,
                  sortOptions
                )
              ]
            : getSearchResultsSortedByCompareFunction(treeNode.children, compareFunction, sortOptions)
        }
      : treeNode
  );
}

/**
 * Returns a list of search results sorted alphabetically, or an empty array if the input is invalid.
 * @param searchResultTreeNodes the results to be sorted.
 */
export function getSearchResultsSortedByRelevancyScore(searchResultTreeNodes: ReadonlyArray<any>): ReadonlyArray<any> {
  return getSearchResultsSortedByCompareFunction(
    searchResultTreeNodes,
    (nodeA, nodeB) => compareNumbers(nodeA.elasticSearchScore, nodeB.elasticSearchScore),
    { sortAscending: false, sortCompaniesOnly: true }
  );
}

/**
 * Takes in an array of search results we store in state and returns an array of
 * unique company keys for companies/people in those results
 */
export function getUniqueCompanyKeysFromSearchResults(searchResults: ReadonlyArray<any>): ReadonlyArray<number> {
  if (!Array.isArray(searchResults) || !searchResults.length) {
    return [];
  }

  const companyKeys = searchResults.reduce((allCompanyKeys, currentResultNode) => {
    const companyKeyForCurrentNode = getCompanyKeyFromNode(currentResultNode);
    if (Array.isArray(currentResultNode.children) && currentResultNode.children.length) {
      return allCompanyKeys.concat(
        companyKeyForCurrentNode ? companyKeyForCurrentNode : [], // filters out undefined companyKeys
        getUniqueCompanyKeysFromSearchResults(currentResultNode.children)
      );
    }

    // filter out undefined companyKey values
    return companyKeyForCurrentNode ? allCompanyKeys.concat(companyKeyForCurrentNode) : allCompanyKeys;
  }, []);

  // now return unique values only
  return Array.from(new Set<number>(companyKeys));
}

export function getUniquePersonIdsFromSearchResults(searchResults: ReadonlyArray<any>): ReadonlyArray<number> {
  if (!Array.isArray(searchResults) || !searchResults.length) {
    return [];
  }

  const personIds = searchResults.reduce((allPersonIds, currentResultNode) => {
    const personIdForCurrentNode = currentResultNode.personKey;
    if (Array.isArray(currentResultNode.children) && currentResultNode.children.length) {
      return allPersonIds.concat(
        personIdForCurrentNode ? personIdForCurrentNode : [], // filters out undefined personIds
        getUniquePersonIdsFromSearchResults(currentResultNode.children)
      );
    }

    // filter out undefined companyKey values
    return personIdForCurrentNode ? allPersonIds.concat(personIdForCurrentNode) : allPersonIds;
  }, []);

  // person ids can have string and number values, convert all to number first,
  // then return unique person ids only
  return Array.from(
    new Set<number>(
      personIds.map((personId: number | string) => (typeof personId === 'string' ? parseInt(personId, 10) : personId))
    )
  );
}

export function mergeTreeNodesAndPowerScoreResults(
  searchResults: ReadonlyArray<any>,
  powerScoreResults: ReadonlyArray<any>
): ReadonlyArray<any> {
  if (!Array.isArray(searchResults) || !searchResults.length) {
    return [];
  }

  // if now powerScore results, return provided search results
  if (!Array.isArray(powerScoreResults) || !powerScoreResults.length) {
    return searchResults;
  }

  const personKeyToPowerScoreMap = powerScoreResults.reduce(
    (mapAccumulator, currentPowerScoreResult) => ({
      ...mapAccumulator,
      [currentPowerScoreResult.personKey]: currentPowerScoreResult.poc
    }),
    {}
  );

  function recursivePowerScoreMerge(results: ReadonlyArray<any>): ReadonlyArray<any> {
    return results.map(result => {
      if (Array.isArray(result.children) && result.children.length > 0) {
        return {
          ...result,
          children: recursivePowerScoreMerge(result.children)
        };
      }

      if (result.isPerson) {
        return { ...result, powerScore: personKeyToPowerScoreMap[result.personKey] || 0 };
      }

      return result;
    });
  }

  return recursivePowerScoreMerge(searchResults);
}

export function getUserCountFromSearchResults(searchResultNodes: ReadonlyArray<any>): number {
  if (!Array.isArray(searchResultNodes) || !searchResultNodes.length) {
    return 0;
  }

  return searchResultNodes.reduce((userCount, currentNode) => {
    const newUserCount = currentNode.isPerson ? userCount + 1 : userCount;
    return newUserCount + getUserCountFromSearchResults(currentNode.children);
  }, 0);
}

export function getCompanyCountFromSearchResults(searchResultNodes: ReadonlyArray<any>): number {
  if (!Array.isArray(searchResultNodes) || !searchResultNodes.length) {
    return 0;
  }

  return searchResultNodes.reduce((companyCount, currentNode) => {
    const newCompanyCount = currentNode.isSupplier ? companyCount + 1 : companyCount;
    return newCompanyCount + getCompanyCountFromSearchResults(currentNode.children);
  }, 0);
}
